import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert
} from '@mui/material';
import { collection, query, getDocs, where, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import { startOfYear, startOfMonth, format, subMonths } from 'date-fns';

interface RevenueData {
  month: string;
  marina: number;
  store: number;
  kitchen: number;
  total: number;
}

interface CustomerSegment {
  type: string;
  percentage: number;
  count: number;
}

interface DepartmentContribution {
  name: string;
  percentage: number;
  revenue: number;
}

const OverviewPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revenueData, setRevenueData] = useState<RevenueData[]>([]);
  const [customerSegments, setCustomerSegments] = useState<CustomerSegment[]>([]);
  const [departmentContribution, setDepartmentContribution] = useState<DepartmentContribution[]>([]);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [growthRate, setGrowthRate] = useState(0);

  useEffect(() => {
    const fetchReportsData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get current date for calculations
        const now = new Date();
        const yearStart = startOfYear(now);
        const lastYearStart = startOfYear(subMonths(now, 12));

        // Fetch reservations for marina revenue
        const reservationsQuery = query(
          collection(db, 'reservations'),
          where('createdAt', '>=', Timestamp.fromDate(yearStart)),
          orderBy('createdAt', 'desc')
        );
        const reservationsSnapshot = await getDocs(reservationsQuery);
        const reservations = reservationsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          finalPrice: doc.data().finalPrice || doc.data().totalPrice || 0
        }));

        // Fetch orders for store and kitchen revenue
        const ordersQuery = query(
          collection(db, 'orders'),
          where('createdAt', '>=', Timestamp.fromDate(yearStart)),
          orderBy('createdAt', 'desc')
        );
        const ordersSnapshot = await getDocs(ordersQuery);
        const orders = ordersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          total: doc.data().total || 0,
          type: doc.data().type || 'RETAIL'
        }));

        // Fetch customers for count and segmentation
        const customersSnapshot = await getDocs(collection(db, 'customers'));
        const customers = customersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          lastVisit: doc.data().lastVisit?.toDate() || null
        }));

        // Calculate monthly revenue data
        const monthlyData: RevenueData[] = [];
        for (let i = 6; i >= 0; i--) {
          const monthDate = subMonths(now, i);
          const monthStart = startOfMonth(monthDate);
          const nextMonthStart = startOfMonth(subMonths(now, i - 1));
          
          const monthReservations = reservations.filter(r => 
            r.createdAt >= monthStart && r.createdAt < nextMonthStart
          );
          const monthOrders = orders.filter(o => 
            o.createdAt >= monthStart && o.createdAt < nextMonthStart
          );

          const marinaRevenue = monthReservations.reduce((sum, r) => sum + (r.finalPrice || 0), 0);
          const storeRevenue = monthOrders
            .filter(o => o.type === 'RETAIL')
            .reduce((sum, o) => sum + (o.total || 0), 0);
          const kitchenRevenue = monthOrders
            .filter(o => o.type === 'DINE_IN' || o.type === 'TAKEOUT')
            .reduce((sum, o) => sum + (o.total || 0), 0);

          monthlyData.push({
            month: format(monthDate, 'MMM'),
            marina: marinaRevenue,
            store: storeRevenue,
            kitchen: kitchenRevenue,
            total: marinaRevenue + storeRevenue + kitchenRevenue
          });
        }

        // Calculate total revenue for the year
        const yearTotalRevenue = monthlyData.reduce((sum, m) => sum + m.total, 0);

        // Calculate department contributions
        const totalMarinaRevenue = monthlyData.reduce((sum, m) => sum + m.marina, 0);
        const totalStoreRevenue = monthlyData.reduce((sum, m) => sum + m.store, 0);
        const totalKitchenRevenue = monthlyData.reduce((sum, m) => sum + m.kitchen, 0);

        const departments: DepartmentContribution[] = [
          {
            name: 'Marina',
            revenue: totalMarinaRevenue,
            percentage: yearTotalRevenue > 0 ? Math.round((totalMarinaRevenue / yearTotalRevenue) * 100) : 0
          },
          {
            name: 'Store',
            revenue: totalStoreRevenue,
            percentage: yearTotalRevenue > 0 ? Math.round((totalStoreRevenue / yearTotalRevenue) * 100) : 0
          },
          {
            name: 'Kitchen',
            revenue: totalKitchenRevenue,
            percentage: yearTotalRevenue > 0 ? Math.round((totalKitchenRevenue / yearTotalRevenue) * 100) : 0
          }
        ];

        // Calculate customer segments
        const sixMonthsAgo = subMonths(now, 6);
        const threeMonthsAgo = subMonths(now, 3);
        
        const recentCustomers = customers.filter(c => c.lastVisit && c.lastVisit >= threeMonthsAgo);
        const activeCustomers = customers.filter(c => c.lastVisit && c.lastVisit >= sixMonthsAgo && c.lastVisit < threeMonthsAgo);
        const newCustomers = customers.filter(c => c.createdAt >= threeMonthsAgo);

        const totalActiveCustomers = Math.max(customers.length, 1); // Prevent division by zero
        const segments: CustomerSegment[] = [
          {
            type: 'Recent Customers',
            count: recentCustomers.length,
            percentage: Math.round((recentCustomers.length / totalActiveCustomers) * 100)
          },
          {
            type: 'Active Customers',
            count: activeCustomers.length,
            percentage: Math.round((activeCustomers.length / totalActiveCustomers) * 100)
          },
          {
            type: 'New Customers',
            count: newCustomers.length,
            percentage: Math.round((newCustomers.length / totalActiveCustomers) * 100)
          }
        ];

        // Calculate growth rate (simplified - comparing last 6 months to previous 6 months)
        const lastSixMonthsRevenue = monthlyData.slice(-6).reduce((sum, m) => sum + m.total, 0);
        const firstSixMonthsRevenue = monthlyData.slice(0, 6).reduce((sum, m) => sum + m.total, 0);
        const calculatedGrowthRate = firstSixMonthsRevenue > 0 
          ? Math.round(((lastSixMonthsRevenue - firstSixMonthsRevenue) / firstSixMonthsRevenue) * 100)
          : 0;

        // Set state
        setRevenueData(monthlyData);
        setDepartmentContribution(departments);
        setCustomerSegments(segments);
        setTotalRevenue(yearTotalRevenue);
        setTotalCustomers(customers.length);
        setGrowthRate(calculatedGrowthRate);

      } catch (err) {
        console.error('Error fetching reports data:', err);
        setError('Failed to load reports data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchReportsData();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Business Overview
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Business Overview
      </Typography>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Total Revenue
              </Typography>
              <Typography variant="h3" sx={{ color: 'primary.main' }}>
                ${totalRevenue.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Year to date
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Customer Count
              </Typography>
              <Typography variant="h3" sx={{ color: 'success.main' }}>
                {totalCustomers.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total customers
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Growth Rate
              </Typography>
              <Typography variant="h3" sx={{ color: growthRate >= 0 ? 'success.main' : 'error.main' }}>
                {growthRate >= 0 ? '+' : ''}{growthRate}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Last 6 months vs previous
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Revenue Chart - Replaced with Table */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Revenue by Department
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Month</TableCell>
                <TableCell align="right">Marina</TableCell>
                <TableCell align="right">Store</TableCell>
                <TableCell align="right">Kitchen</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {revenueData.map((data) => (
                <TableRow key={data.month}>
                  <TableCell>{data.month}</TableCell>
                  <TableCell align="right">${data.marina.toLocaleString()}</TableCell>
                  <TableCell align="right">${data.store.toLocaleString()}</TableCell>
                  <TableCell align="right">${data.kitchen.toLocaleString()}</TableCell>
                  <TableCell align="right"><strong>${data.total.toLocaleString()}</strong></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {/* Pie Charts replaced with tables */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Customer Distribution
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Customer Type</TableCell>
                    <TableCell align="right">Count</TableCell>
                    <TableCell align="right">Percentage</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customerSegments.map((segment) => (
                    <TableRow key={segment.type}>
                      <TableCell>{segment.type}</TableCell>
                      <TableCell align="right">{segment.count}</TableCell>
                      <TableCell align="right">{segment.percentage}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Revenue by Department
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Department</TableCell>
                    <TableCell align="right">Revenue</TableCell>
                    <TableCell align="right">Contribution</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {departmentContribution.map((dept) => (
                    <TableRow key={dept.name}>
                      <TableCell>{dept.name}</TableCell>
                      <TableCell align="right">${dept.revenue.toLocaleString()}</TableCell>
                      <TableCell align="right">{dept.percentage}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OverviewPage; 